import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import UserProfile from './UserProfile';
import NotificationBell from './NotificationBell';
import { useAuth } from '../../hooks/useAuth';
import { PATH_PAGES } from '../../utils/pageAccess';
import './Navbar.css';

const MOBILE_QUERY = '(max-width: 900px)';
const COLLAPSE_KEY = 'navbar-collapsed';

/* На мобильном сайдбар — выезжающая шторка, а колокольчик и профиль
   переезжают в компактный топбар: держать оба места смонтированными
   одновременно означало бы двойной опрос уведомлений. */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

/* Иконки рисуем штрихом вместо эмодзи: они наследуют цвет ссылки,
   одинаковы по весу и не «пляшут» между операционными системами. */
const Icon = ({ children }) => (
  <svg
    className="navbar-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const navLinks = [
  {
    path: '/dashboard', name: 'Дашборд', title: 'Дашборд выручки',
    icon: <Icon><path d="M3 22H21" /><path d="M3 11C3 10.0572 3 9.58579 3.29289 9.29289C3.58579 9 4.05719 9 5 9C5.94281 9 6.41421 9 6.70711 9.29289C7 9.58579 7 10.0572 7 11V17C7 17.9428 7 18.4142 6.70711 18.7071C6.41421 19 5.94281 19 5 19C4.05719 19 3.58579 19 3.29289 18.7071C3 18.4142 3 17.9428 3 17V11Z" /><path d="M10 7C10 6.05719 10 5.58579 10.2929 5.29289C10.5858 5 11.0572 5 12 5C12.9428 5 13.4142 5 13.7071 5.29289C14 5.58579 14 6.05719 14 7V17C14 17.9428 14 18.4142 13.7071 18.7071C13.4142 19 12.9428 19 12 19C11.0572 19 10.5858 19 10.2929 18.7071C10 18.4142 10 17.9428 10 17V7Z" /><path d="M17 4C17 3.05719 17 2.58579 17.2929 2.29289C17.5858 2 18.0572 2 19 2C19.9428 2 20.4142 2 20.7071 2.29289C21 2.58579 21 3.05719 21 4V17C21 17.9428 21 18.4142 20.7071 18.7071C20.4142 19 19.9428 19 19 19C18.0572 19 17.5858 19 17.2929 18.7071C17 18.4142 17 17.9428 17 17V4Z" /></Icon>
  },
  {
    path: '/online', name: 'Онлайн', title: 'Живой зал',
    icon: <Icon><circle cx="12" cy="12" r="3" /><path d="M12 2V5" /><path d="M12 19V22" /><path d="M4.92896 4.92896L7.05026 7.05026" /><path d="M16.9497 16.9497L19.071 19.071" /><path d="M2 12H5" /><path d="M19 12H22" /><path d="M4.92896 19.071L7.05026 16.9497" /><path d="M16.9497 7.05026L19.071 4.92896" /></Icon>
  },
  {
    path: '/writeoffs', name: 'Списания', title: 'Списания',
    icon: <Icon><path d="M9.1709 4C9.58273 2.83481 10.694 2 12.0002 2C13.3064 2 14.4177 2.83481 14.8295 4" /><path d="M20.5001 6H3.5" /><path d="M18.8332 8.5L18.3732 15.3991C18.1962 18.054 18.1077 19.3815 17.2427 20.1907C16.3777 21 15.0473 21 12.3865 21H11.6132C8.95235 21 7.62195 21 6.75694 20.1907C5.89194 19.3815 5.80344 18.054 5.62644 15.3991L5.1665 8.5" /><path d="M9.5 11L10 16" /><path d="M14.5 11L14 16" /></Icon>
  },
  {
    path: '/kitchen', name: 'Кухня', title: 'Кухня и бар',
    icon: <Icon><path d="M5 14.584C3.2341 13.8124 2 12.0503 2 10C2 7.23858 4.23858 5 7 5C7.25052 5 7.49673 5.01842 7.73736 5.05399" /><path d="M19 14.584V18C19 19.8856 19 20.8285 18.4142 21.4142C17.8284 22 16.8856 22 15 22H9C7.11438 22 6.17157 22 5.58579 21.4142C5 20.8285 5 19.8856 5 18V14.584" /><path d="M16.2627 5.05399C16.5033 5.01842 16.7495 5 17.0001 5C19.7615 5 22.0001 7.23858 22.0001 10C22.0001 12.0503 20.766 13.8124 19.0001 14.584" /><path d="M16.5 7V6.5C16.5 5.99416 16.4165 5.50782 16.2626 5.05399C15.6604 3.27806 13.9794 2 12 2C10.0206 2 8.33962 3.27806 7.73736 5.05399C7.58346 5.50782 7.5 5.99416 7.5 6.5V7" /><path d="M5 18H19" /></Icon>
  },
  {
    path: '/kitchen-screen', name: 'Кухонный экран', title: 'Кухонный экран',
    icon: <Icon><path d="M5 14.584C3.2341 13.8124 2 12.0503 2 10C2 7.23858 4.23858 5 7 5C7.25052 5 7.49673 5.01842 7.73736 5.05399" /><path d="M19 14.584V18C19 19.8856 19 20.8285 18.4142 21.4142C17.8284 22 16.8856 22 15 22H9C7.11438 22 6.17157 22 5.58579 21.4142C5 20.8285 5 19.8856 5 18V14.584" /><path d="M16.2627 5.05399C16.5033 5.01842 16.7495 5 17.0001 5C19.7615 5 22.0001 7.23858 22.0001 10C22.0001 12.0503 20.766 13.8124 19.0001 14.584" /><path d="M16.5 7V6.5C16.5 5.99416 16.4165 5.50782 16.2626 5.05399C15.6604 3.27806 13.9794 2 12 2C10.0206 2 8.33962 3.27806 7.73736 5.05399C7.58346 5.50782 7.5 5.99416 7.5 6.5V7" /><path d="M5 18H19" /></Icon>
  },
  {
    path: '/employees', name: 'Сотрудники', title: 'Сотрудники',
    icon: <Icon><circle cx="9" cy="6" r="4" /><path d="M15 9C16.6569 9 18 7.65685 18 6C18 4.34315 16.6569 3 15 3" /><ellipse cx="9" cy="17" rx="7" ry="4" /><path d="M18 14C19.7542 14.3847 21 15.3589 21 16.5C21 17.5293 19.9863 18.4229 18.5 18.8704" /></Icon>
  },
  {
    path: '/cash', name: 'Касса', title: 'Касса',
    icon: <Icon><path d="M18.6667 11C20.5513 10.7213 22 9.04574 22 7.02036C22 4.79998 20.2589 3 18.1111 3H5.88889C3.74112 3 2 4.79998 2 7.02036C2 9.04574 3.44873 10.7213 5.33333 11" /><path d="M12 6V13M10 10.6667L12 13L14 10.6667" /><path d="M5 10C5 8.11438 5 7.17157 5.58579 6.58579C6.17157 6 7.11438 6 9 6H15C16.8856 6 17.8284 6 18.4142 6.58579C19 7.17157 19 8.11438 19 10V16C19 17.8856 19 18.8284 18.4142 19.4142C17.8284 20 16.8856 20 15 20H9C7.11438 20 6.17157 20 5.58579 19.4142C5 18.8284 5 17.8856 5 16V10Z" /><path d="M5 17H19" /></Icon>
  },
  {
    path: '/receipts', name: 'Чеки', title: 'Закрытые чеки',
    icon: <Icon><path d="M16.755 2H7.24502C6.08614 2 5.50671 2 5.03939 2.16261C4.15322 2.47096 3.45748 3.18719 3.15795 4.09946C3 4.58055 3 5.17705 3 6.37006V20.3742C3 21.2324 3.985 21.6878 4.6081 21.1176C4.97417 20.7826 5.52583 20.7826 5.8919 21.1176L6.375 21.5597C7.01659 22.1468 7.98341 22.1468 8.625 21.5597C9.26659 20.9726 10.2334 20.9726 10.875 21.5597C11.5166 22.1468 12.4834 22.1468 13.125 21.5597C13.7666 20.9726 14.7334 20.9726 15.375 21.5597C16.0166 22.1468 16.9834 22.1468 17.625 21.5597L18.1081 21.1176C18.4742 20.7826 19.0258 20.7826 19.3919 21.1176C20.015 21.6878 21 21.2324 21 20.3742V6.37006C21 5.17705 21 4.58055 20.842 4.09946C20.5425 3.18719 19.8468 2.47096 18.9606 2.16261C18.4933 2 17.9139 2 16.755 2Z" /><path d="M10.5 11L17 11" /><path d="M7 11H7.5" /><path d="M7 7.5H7.5" /><path d="M7 14.5H7.5" /><path d="M10.5 7.5H17" /><path d="M10.5 14.5H17" /></Icon>
  },
  {
    path: '/kpi', name: 'KPI', title: 'KPI сотрудников',
    icon: <Icon><path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" /><path d="M2 12L5 12" /><path d="M19 12L22 12" /><path d="M12 22L12 19" /><path d="M12 5L12 2" /><path d="M10 12H12H14" /><path d="M12 14L12 12L12 10" /></Icon>
  },
  {
    path: '/crm', name: 'CRM', title: 'Гости, лояльность, задачи и сегменты',
    icon: <Icon><path d="M2 4.99792V16.1437C2 17.2547 2.90605 18.1536 4.01486 18.2229C4.98428 18.2836 6.12329 18.4021 7 18.6335C8.08145 18.9189 9.37293 19.5366 10.3724 20.0694C11.3852 20.6092 12.6148 20.6092 13.6276 20.0694C14.6271 19.5366 15.9185 18.9189 17 18.6335C17.8767 18.4021 19.0157 18.2836 19.9851 18.2229C21.094 18.1536 22 17.2547 22 16.1437V4.93332C22 3.86087 21.1538 2.98053 20.082 3.01787C18.9534 3.05718 17.5469 3.17415 16.5 3.48757C15.5924 3.75929 14.5353 4.30431 13.6738 4.80287C12.6355 5.40379 11.3428 5.43636 10.2823 4.87558C9.29611 4.35413 8.04921 3.76443 7 3.48757C6.11349 3.25363 4.95877 3.13502 3.9824 3.07501C2.8863 3.00764 2 3.89975 2 4.99792Z" /><path d="M12 5.50049V20.5005" /></Icon>
  },
  {
    path: '/purchases', name: 'Закупки', title: 'Закупки и согласования',
    icon: <Icon><path d="M2 3L2.26491 3.0883C3.58495 3.52832 4.24497 3.74832 4.62248 4.2721C5 4.79587 5 5.49159 5 6.88304V9.5C5 12.3284 5 13.7426 5.87868 14.6213C6.75736 15.5 8.17157 15.5 11 15.5H19" /><path d="M7.5 18C8.32843 18 9 18.6716 9 19.5C9 20.3284 8.32843 21 7.5 21C6.67157 21 6 20.3284 6 19.5C6 18.6716 6.67157 18 7.5 18Z" /><path d="M16.5 18.0001C17.3284 18.0001 18 18.6716 18 19.5001C18 20.3285 17.3284 21.0001 16.5 21.0001C15.6716 21.0001 15 20.3285 15 19.5001C15 18.6716 15.6716 18.0001 16.5 18.0001Z" /><path d="M11 9H8" /><path d="M5 6H16.4504C18.5054 6 19.5328 6 19.9775 6.67426C20.4221 7.34853 20.0173 8.29294 19.2078 10.1818L18.7792 11.1818C18.4013 12.0636 18.2123 12.5045 17.8366 12.7523C17.4609 13 16.9812 13 16.0218 13H5" /></Icon>
  },
  {
    path: '/messages', name: 'Чат', title: 'Сообщения',
    icon: <Icon><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22Z" /><path d="M8 12H8.009M11.991 12H12M15.991 12H16" /></Icon>
  },
  {
    path: '/support', name: 'Поддержка', title: 'Техподдержка',
    icon: <Icon><path d="M21 17V12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12V17" /><path d="M22 15.5V17.5" /><path d="M2 15.5V17.5" /><path d="M8 13.8446C8 13.0802 8 12.698 7.82526 12.4323C7.73733 12.2985 7.62061 12.188 7.4844 12.1095C7.21371 11.9535 6.84812 11.9896 6.11694 12.0617C4.88487 12.1831 4.26884 12.2439 3.82737 12.5764C3.60394 12.7448 3.41638 12.9593 3.27646 13.2067C3 13.6955 3 14.3395 3 15.6276V17.1933C3 18.4685 3 19.1061 3.28198 19.5986C3.38752 19.7829 3.51981 19.9491 3.67416 20.0913C4.08652 20.4714 4.68844 20.5901 5.89227 20.8275C6.73944 20.9945 7.16302 21.078 7.47564 20.9021C7.591 20.8372 7.69296 20.7493 7.77572 20.6434C8 20.3565 8 19.9078 8 19.0104V13.8446Z" /><path d="M16 13.8446C16 13.0802 16 12.698 16.1747 12.4323C16.2627 12.2985 16.3794 12.188 16.5156 12.1095C16.7863 11.9535 17.1519 11.9896 17.8831 12.0617C19.1151 12.1831 19.7312 12.2439 20.1726 12.5764C20.3961 12.7448 20.5836 12.9593 20.7235 13.2067C21 13.6955 21 14.3395 21 15.6276V17.1933C21 18.4685 21 19.1061 20.718 19.5986C20.6125 19.7829 20.4802 19.9491 20.3258 20.0913C19.9135 20.4714 19.3116 20.5901 18.1077 20.8275C17.2606 20.9945 16.837 21.078 16.5244 20.9021C16.409 20.8372 16.307 20.7493 16.2243 20.6434C16 20.3565 16 19.9078 16 19.0104V13.8446Z" /></Icon>
  },
  {
    /* Справка стоит рядом с поддержкой намеренно: человек, который ищет
       помощь, идёт в одно и то же место, и половина обращений в поддержку
       снимается статьёй, до которой он дошёл сам. */
    path: '/help', name: 'Справка', title: 'Как всё устроено и что делать, если не работает',
    icon: <Icon><circle cx="12" cy="12" r="10" /><path d="M10.125 8.875C10.125 7.83947 10.9645 7 12 7C13.0355 7 13.875 7.83947 13.875 8.875C13.875 9.56245 13.505 10.1635 12.9534 10.4899C12.478 10.7711 12 11.1977 12 11.75V13" /><path d="M12 16H12.0001" /></Icon>
  }
];

/* Модули, доступ к которым ограничен тарифом — скрываем пункт меню, если у
   организации нет фичи (только для уже оплаченных статусов, в триале видно всё). */
const PLAN_GATED_LINKS = { '/crm': 'has_crm', '/kpi': 'has_kpi' };


/* Разделы без вкладки в меню — им тоже нужен подзаголовок в шапке */
const extraTitles = { '/profile': 'Мой профиль', '/users': 'Пользователи', '/billing': 'Биллинг' };

/* Инициалы для логотипа из названия организации: "Центральные бани" -> "ЦБ" */
const orgInitials = (name) => {
  if (!name) return '';
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map(w => w[0]).join('').toUpperCase();
};

const Navbar = ({ user, isAdmin, onLogout }) => {
  const { organization, canAccess, moduleBlock } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  // Логин и другие страницы без меню не должны получать отступ под сайдбар
  useEffect(() => {
    document.body.classList.add('has-sidebar');
    return () => document.body.classList.remove('has-sidebar');
  }, []);

  // Переход на другую страницу закрывает мобильную шторку
  useEffect(() => setMobileOpen(false), [location.pathname]);

  // Escape закрывает шторку; клик обрабатывает затемняющий фон под ней
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  // Свёрнутый режим (иконки без подписей) — только на десктопе, и запоминаем выбор
  useEffect(() => {
    document.documentElement.classList.toggle('sidebar-collapsed', collapsed && !isMobile);
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch {}
    return () => document.documentElement.classList.remove('sidebar-collapsed');
  }, [collapsed, isMobile]);

  const visibleLinks = navLinks.filter(({ path }) => {
    // Какие разделы видит роль, настраивает администратор в «Пользователи → Роли»
    if (!canAccess(PATH_PAGES[path])) return false;
    const feature = PLAN_GATED_LINKS[path];
    if (!feature || organization?.status !== 'active' || !organization?.plan) return true;
    return organization.plan[feature] !== false;
  });

  const current = navLinks.find(link => link.path === location.pathname);
  const pageTitle = current?.title || extraTitles[location.pathname] || 'Обзор';

  const closeMobile = () => setMobileOpen(false);

  const adminLinks = (
    <>
      {isAdmin && (
        <Link
          to="/billing"
          className={`navbar-link ${location.pathname === '/billing' ? 'active' : ''}`}
          title="Биллинг"
          onClick={closeMobile}
        >
          <Icon><path d="M2 12C2 8.22876 2 6.34315 3.17157 5.17157C4.34315 4 6.22876 4 10 4H14C17.7712 4 19.6569 4 20.8284 5.17157C22 6.34315 22 8.22876 22 12C22 15.7712 22 17.6569 20.8284 18.8284C19.6569 20 17.7712 20 14 20H10C6.22876 20 4.34315 20 3.17157 18.8284C2 17.6569 2 15.7712 2 12Z" /><path d="M10 16H6" /><path d="M14 16H12.5" /><path d="M2 10L22 10" /></Icon>
          <span className="navbar-link-label">Биллинг</span>
        </Link>
      )}
      {isAdmin && (
        <Link
          to="/users"
          className={`navbar-link ${location.pathname === '/users' ? 'active' : ''}`}
          title="Пользователи"
          onClick={closeMobile}
        >
          <Icon><path d="M3 10.4167C3 7.21907 3 5.62028 3.37752 5.08241C3.75503 4.54454 5.25832 4.02996 8.26491 3.00079L8.83772 2.80472C10.405 2.26824 11.1886 2 12 2C12.8114 2 13.595 2.26824 15.1623 2.80472L15.7351 3.00079C18.7417 4.02996 20.245 4.54454 20.6225 5.08241C21 5.62028 21 7.21907 21 10.4167C21 10.8996 21 11.4234 21 11.9914C21 17.6294 16.761 20.3655 14.1014 21.5273C13.38 21.8424 13.0193 22 12 22C10.9807 22 10.62 21.8424 9.89856 21.5273C7.23896 20.3655 3 17.6294 3 11.9914C3 11.4234 3 10.8996 3 10.4167Z" /><circle cx="12" cy="9" r="2" /><path d="M16 15C16 16.1046 16 17 12 17C8 17 8 16.1046 8 15C8 13.8954 9.79086 13 12 13C14.2091 13 16 13.8954 16 15Z" /></Icon>
          <span className="navbar-link-label">Пользователи</span>
        </Link>
      )}
    </>
  );

  return (
    <>
      <header className="navbar-topbar">
        <button
          className={`navbar-burger ${mobileOpen ? 'open' : ''}`}
          type="button"
          onClick={() => setMobileOpen(open => !open)}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}
        >
          <span /><span /><span />
        </button>

        <Link to="/dashboard" className="navbar-topbar-brand">
          <span className="navbar-logo" aria-hidden="true">{orgInitials(organization?.name)}</span>
          <span className="navbar-topbar-title">{pageTitle}</span>
        </Link>

        {isMobile && (
          <div className="navbar-topbar-utils">
            <NotificationBell />
            <UserProfile user={user} onLogout={onLogout} />
          </div>
        )}
      </header>

      {mobileOpen && <div className="navbar-backdrop" onClick={closeMobile} aria-hidden="true" />}

      <aside
        className={`navbar-sidebar ${collapsed && !isMobile ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}
        aria-label="Основная навигация"
      >
        <div className="navbar-sidebar-head">
          <Link to="/dashboard" className="navbar-brand" onClick={closeMobile}>
            <span className="navbar-logo" aria-hidden="true">{orgInitials(organization?.name)}</span>
            <span className="navbar-brand-text">
              <span className="navbar-brand-name">{organization?.name || '…'}</span>
              <span className="navbar-brand-page">{pageTitle}</span>
            </span>
          </Link>
          {!isMobile && (
            <button
              className="navbar-collapse-btn"
              type="button"
              onClick={() => setCollapsed(c => !c)}
              title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
              aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            >
              <Icon><path d={collapsed ? 'M9 5L15 12L9 19' : 'M15 5L9 12L15 19'} /></Icon>
            </button>
          )}
        </div>

        <nav className="navbar-sidebar-nav" aria-label="Разделы">
          {visibleLinks.map(link => {
            const active = location.pathname === link.path;
            // Отключённый платформой раздел не прячем: по клику человек увидит причину
            const block = moduleBlock(PATH_PAGES[link.path]);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar-link ${active ? 'active' : ''} ${block ? 'navbar-link--off' : ''}`}
                title={block ? `${link.name} — отключён: ${block.reason}` : link.name}
                aria-current={active ? 'page' : undefined}
                onClick={closeMobile}
              >
                {link.icon}
                <span className="navbar-link-label">{link.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="navbar-sidebar-foot">
          {adminLinks}
          {isAdmin && <div className="navbar-sidebar-divider" />}
          {!isMobile && (
            <div className="navbar-sidebar-utils">
              <NotificationBell />
              <UserProfile user={user} onLogout={onLogout} />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Navbar;
